import { CRORE } from "../model/types.ts";

export interface WaterfallStep {
  label: string;
  /** Positive for the opening and closing totals; the size of the deduction otherwise. */
  value: number;
  kind: "total" | "deduction";
  tone: "total" | "lost" | "ustax" | "penalty" | "fees" | "india";
}

const W = 760;
const H = 300;
const PAD = { l: 58, r: 12, t: 26, b: 46 };

/**
 * Gross → each deduction → net, in rupees. Every bar sits on the running total
 * so the eye can follow where the money goes, and each is labelled with its
 * name and value so colour is never the only cue.
 */
export function Waterfall({ steps }: { steps: WaterfallStep[] }) {
  const top = Math.max(...steps.filter((s) => s.kind === "total").map((s) => s.value), 1);
  const yMax = niceMax(top / CRORE);
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const slot = iw / steps.length;
  const barW = Math.min(64, slot * 0.62);
  const y = (cr: number) => PAD.t + ih * (1 - cr / yMax);

  let running = 0;
  const bars = steps.map((s, i) => {
    const v = s.value / CRORE;
    let from: number;
    let to: number;
    if (s.kind === "total") {
      from = 0;
      to = v;
      running = v;
    } else {
      from = running - v;
      to = running;
      running = from;
    }
    const x = PAD.l + slot * i + (slot - barW) / 2;
    return { ...s, x, from, to, v, i };
  });

  const ticks = Array.from({ length: 5 }, (_, i) => (yMax / 4) * i);

  return (
    <figure className="chart waterfall">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={describe(steps)}>
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid" x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} />
            <text className="tick" x={PAD.l - 8} y={y(t) + 3.5} textAnchor="end">
              {t.toFixed(t < 1 ? 1 : 0)}
            </text>
          </g>
        ))}
        <text className="tick" x={PAD.l - 8} y={PAD.t - 12} textAnchor="end">₹ cr</text>

        {bars.map((b, idx) => {
          const yTop = y(Math.max(b.from, b.to));
          const hgt = Math.max(1, Math.abs(y(b.from) - y(b.to)));
          const next = bars[idx + 1];
          return (
            <g key={b.label}>
              <rect className={`wf-${b.tone}`} x={b.x} y={yTop} width={barW} height={hgt} rx={1.5} />
              {next && (
                <line
                  className="wf-connector"
                  x1={b.x + barW}
                  x2={next.x}
                  y1={y(b.kind === "total" ? b.to : b.from)}
                  y2={y(b.kind === "total" ? b.to : b.from)}
                />
              )}
              <text className="wf-value" x={b.x + barW / 2} y={yTop - 6} textAnchor="middle">
                {b.kind === "deduction" ? (b.v > 0 ? `−${b.v.toFixed(2)}` : "0") : b.v.toFixed(2)}
              </text>
              <text className="tick wf-label" x={b.x + barW / 2} y={H - PAD.b + 16} textAnchor="middle">
                {b.label.split("\n").map((line, j) => (
                  <tspan key={j} x={b.x + barW / 2} dy={j === 0 ? 0 : 12}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
        <line className="axis" x1={PAD.l} x2={W - PAD.r} y1={y(0)} y2={y(0)} />
      </svg>
    </figure>
  );
}

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * mag >= v * 1.08) return m * mag;
  return 10 * mag;
}

function describe(steps: WaterfallStep[]): string {
  const f = (v: number) => `${(v / CRORE).toFixed(2)} crore rupees`;
  return (
    "Waterfall from gross assets to net rupees in India: " +
    steps.map((s) => `${s.label.replace("\n", " ")} ${s.kind === "deduction" ? "minus " : ""}${f(s.value)}`).join(", ") +
    "."
  );
}
